import {
  ITransferGroupFormFieldPath,
  ObjectId,
  ITransferData,
  ITransferDestination,
  IBaseEntityType,
  IQohInventoryItemVenue,
  ITransferItem,
} from '../../../types';
import {
  getFormFieldValue,
  setFormFieldValue,
} from '../../../../components/form/services/schema-form/schema-form-functions';
import {
  has,
  includes,
  map,
  unset,
  get,
  concat,
  set,
  assign,
  orderBy,
  toLower,
  chain,
} from 'lodash';
import { FormGroup } from '@angular/forms';
import {
  ITreeConverterConfig,
  ITreeConverterReportCategoryAlias,
  ITreeData,
} from '@app/shared/types/common/tree.type';
import { generateId } from '@app/shared/utils/common-utils';
import { COMMON_FORM_PATH } from '@app/shared/constants/form-paths';

export const EMPTY_CHANGED_ITEMS = {
  items: [],
  grand_total: {
    received_cost: 0,
    request_cost: 0,
    reversed_cost: 0,
  },
};

export const markTransferItemsAcknowledge = (
  transferIds: ObjectId[],
  form: FormGroup,
  formFieldPath: ITransferGroupFormFieldPath,
): void => {
  const transferItems = getFormFieldValue(
    form,
    formFieldPath.virtualTransferItems,
  );
  const markedTransferItems = map(transferItems, (transferItem) => {
    if (
      includes(transferIds, transferItem._id) &&
      !has(transferItem, 'updated_items') &&
      !has(transferItem, 'unavailable_items')
    ) {
      return {
        ...transferItem,
        updated_items: {},
      };
    }

    return transferItem;
  });

  setFormFieldValue(
    form,
    formFieldPath.virtualTransferItems,
    markedTransferItems,
    false,
  );
};

export const unmarkTransferItemsAcknowledge = (
  transferIds: ObjectId[],
  form: FormGroup,
  formFieldPath: ITransferGroupFormFieldPath,
): void => {
  const transferItems = getFormFieldValue(
    form,
    formFieldPath.virtualTransferItems,
  );
  const unmarkedTransferItems = map(transferItems, (transferItem) => {
    if (includes(transferIds, transferItem._id)) {
      unset(transferItem, 'updated_items');
      unset(transferItem, 'unavailable_items');
    }

    return transferItem;
  });

  setFormFieldValue(
    form,
    formFieldPath.virtualTransferItems,
    unmarkedTransferItems,
    false,
  );
};

/**
 * push new => open transfers to document transfers
 *
 * @param doc target doc
 * @param newTransfers new transfers(form new => open)
 */
export const pushNewTransfersToDoc = (
  doc: IBaseEntityType,
  newTransfers: Partial<ITransferData | ITransferDestination>[],
): void => {
  const transfersPath = 'virtual.transfer_items';

  let currentTransfers = get(doc, transfersPath, []);

  currentTransfers = concat(currentTransfers, newTransfers);

  set(doc, transfersPath, currentTransfers);
};

export const getSortedTreeDataByConfig = (
  data: IQohInventoryItemVenue[] | ITransferItem[],
  config: ITreeConverterConfig,
): ITreeData[] => {
  // The path used for sorting and grouping
  const path: string = config.path;
  // The groupName used for group naming
  const groupName: string = config.groupName;
  const majorAlias: ITreeConverterReportCategoryAlias = config.majorAlias || {};
  const minorAlias: ITreeConverterReportCategoryAlias = config.minorAlias || {};
  const newItems = map(data, (item) => {
    return assign(
      {},
      item,
      {
        internal_grid_row_id: `local${generateId()}`,
        isLeaf: true,
        ...config.additional,
      },
      majorAlias.backup ? { [majorAlias.backup]: item[majorAlias.origin] } : {},
      minorAlias.backup ? { [minorAlias.backup]: item[minorAlias.origin] } : {},
      config.additionalFn ? config.additionalFn(item) : {},
    );
  });
  const sortItems = orderBy(
    newItems,
    ['is_chargeable', (item): string => toLower(get(item, path, ''))],
    ['desc', 'asc'],
  );

  if (!groupName) return sortItems;

  return chain(sortItems)
    .groupBy(path)
    .toPairs()
    .map(([name, children]) => {
      const childrenConfig = config.childrenConfig;
      const newChildren = getSortedTreeDataByConfig(children, childrenConfig);

      return {
        [groupName]: name,
        children: newChildren,
        error: [],
        ...config.additionalTreeNode,
        ...(config.additionalTreeFn ? config.additionalTreeFn(name) : {}),
        isLeaf: false,
      };
    })
    .sortBy((item): string => toLower(get(item, groupName, '')))
    .value();
};

export const convertInventoryItemVenuesToTree = (
  items: IQohInventoryItemVenue[],
): ITreeData[] => {
  return getSortedTreeDataByConfig(items, {
    path: COMMON_FORM_PATH.reportingCategoryMajorName,
    groupName: 'name',
    additionalTreeFn: (name): IBaseEntityType => {
      return {
        isMajorCategoryRow: true,
        reporting_category_major: {
          name,
        },
      };
    },
    childrenConfig: {
      path: COMMON_FORM_PATH.reportingCategoryMinorName,
      additionalTreeFn: (name): IBaseEntityType => {
        return {
          isMinorCategoryRow: true,
          reporting_category_minor: {
            name,
          },
        };
      },
      groupName: 'name',
      childrenConfig: {
        path: 'name',
      },
    },
  });
};
